import { useIsCodelistFeatureEnabled } from 'features/codelist/useIsCodeListEnabled';
import { Button } from 'features/components/Button';
import { Dialog } from 'features/entry-form-v2/component/Dialog';
import { useLanguage } from 'features/hooks/useLanguage';
import { useLocalStorage } from 'features/hooks/useLocalStorage';
import { useState } from 'react';
import { LanguageType } from 'types';
import { IntroSection } from './IntroSection';
import { NotAsVariablesSection } from './NotAsVariablesSection';
import { OpenCodelistSection } from './OpenCodelistSection';
import { SearchCodelistSection } from './SearchCodelistSection';

export const CodelistProjectDialogContainer = () => {
	const [hasSeenCodelistProjectDialog, setHasSeenCodelistProjectDialog] = useLocalStorage(
		'has_seen_codelist_project_dialog',
		false
	);

	const codelistEnabled = useIsCodelistFeatureEnabled();

	if (!codelistEnabled) return null;

	return (
		<CodelistProjectDialog
			isOpen={!hasSeenCodelistProjectDialog}
			onClose={() => {
				setHasSeenCodelistProjectDialog(true);
			}}
		/>
	);
};

const CodelistProjectDialog = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
	const currentStep = STEPS[currentStepIndex];

	return (
		<Dialog open={isOpen} onClose={onClose} title={translation.intro.title}>
			<div className="w-[600px] h-[600px] flex flex-col gap-6 justify-between">
				<div>
					{currentStep === 'intro' && <IntroSection />}
					{currentStep === 'not-as-variables' && <NotAsVariablesSection />}
					{currentStep === 'open-codelist' && <OpenCodelistSection />}
					{currentStep === 'search-codelists' && <SearchCodelistSection />}
				</div>

				<div className="flex justify-between items-end">
					<p>{translation.step_x_of_total(currentStepIndex + 1, STEPS.length)}</p>

					<div className="flex gap-2 flex-row">
						{currentStepIndex > 0 && (
							<Button
								onClick={() => setCurrentStepIndex(currentStepIndex - 1)}
								variant="secondary"
								text={translation.previous}
							/>
						)}

						{currentStepIndex < STEPS.length - 1 && (
							<Button
								onClick={() => setCurrentStepIndex(currentStepIndex + 1)}
								text={translation.next}
							/>
						)}

						{currentStepIndex === STEPS.length - 1 && (
							<Button onClick={onClose} text={translation.close} />
						)}
					</div>
				</div>
			</div>
		</Dialog>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		intro: {
			title: 'Personal identifiable information in Ledidi'
		},

		step_x_of_total: (step: number, totalSteps: number) => `Step ${step} of ${totalSteps}`,

		previous: 'Previous',
		next: 'Next',
		close: 'Close'
	},

	[LanguageType.Norwegian]: {
		intro: {
			title: 'Personlig identifiserbar informasjon i Ledidi'
		},

		step_x_of_total: (step: number, totalSteps: number) => `Steg ${step} av ${totalSteps}`,

		previous: 'Forrige',
		next: 'Neste',
		close: 'Lukk'
	}
};

const STEPS = ['intro', 'not-as-variables', 'open-codelist', 'search-codelists'];
